import { useMemo } from "react";
import { SearchNormal } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { AssignableBusiness } from "./AssignAccountManagerModal";

type ManageMemberCustomersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  businesses: AssignableBusiness[];
  selectedBusinessIds: string[];
  query: string;
  onQueryChange: (value: string) => void;
  onToggleBusiness: (businessId: string) => void;
  onBack: () => void;
  onReassignCustomers: () => void;
};

const ManageMemberCustomersModal = ({
  open,
  onOpenChange,
  memberName,
  businesses,
  selectedBusinessIds,
  query,
  onQueryChange,
  onToggleBusiness,
  onBack,
  onReassignCustomers,
}: ManageMemberCustomersModalProps) => {
  const filteredBusinesses = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return businesses;
    return businesses.filter((business) => {
      const nameMatch = business.name.toLowerCase().includes(normalized);
      const email = business.email ?? business.user?.email ?? "";
      return nameMatch || email.toLowerCase().includes(normalized);
    });
  }, [businesses, query]);

  const groupedBusinesses = useMemo(() => {
    return filteredBusinesses.reduce<Record<string, AssignableBusiness[]>>(
      (acc, business) => {
        const key = business.name.charAt(0).toUpperCase() || "#";
        if (!acc[key]) acc[key] = [];
        acc[key].push(business);
        return acc;
      },
      {},
    );
  }, [filteredBusinesses]);

  const orderedGroupKeys = useMemo(
    () => Object.keys(groupedBusinesses).sort((a, b) => a.localeCompare(b)),
    [groupedBusinesses],
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader className="relative justify-center">
        <div className="text-center">
          <div className="text-[22px] font-[500] text-[#3A3E5B]">{memberName}</div>
          <ModalTitle>Managed customers</ModalTitle>
        </div>
        <button
          type="button"
          aria-label="Close modal"
          className="absolute right-6 top-6 rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onBack}
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="space-y-4 pt-4">
        <div className="flex h-12 items-center gap-2 rounded-[12px] bg-[#E9ECFA] px-3">
          <SearchNormal size={18} className="text-[#737AA8]" />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search by name or email"
            className="w-full bg-transparent text-[16px] text-[#2C2F44] placeholder:text-[#737AA8] focus:outline-none"
          />
        </div>

        <div className="max-h-[56vh] space-y-4 overflow-y-auto pr-1">
          {orderedGroupKeys.length === 0 ? (
            <div className="rounded-[12px] px-3 py-4 text-[14px] text-[#6B6F8A]">
              No customers found.
            </div>
          ) : (
            orderedGroupKeys.map((groupKey) => (
              <div key={groupKey}>
                <div className="mb-2 text-[20px] font-[500] text-[#6E7397]">{groupKey}</div>
                <div className="space-y-2 rounded-[16px] bg-white p-3">
                  {groupedBusinesses[groupKey].map((business) => {
                    const isSelected = selectedBusinessIds.includes(business.id);
                    const initials = business.name
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((part) => part.charAt(0).toUpperCase())
                      .join("");

                    return (
                      <button
                        key={business.id}
                        type="button"
                        onClick={() => onToggleBusiness(business.id)}
                        className="flex w-full items-center gap-3 rounded-[12px] px-2 py-2 text-left hover:bg-[#F7F8FD]"
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => onToggleBusiness(business.id)}
                          size="sm"
                        />
                        <div className="grid h-10 w-10 place-items-center rounded-full bg-[#EEF1FB] text-[16px] font-[600] text-[#3A3E5B]">
                          {initials || business.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-[16px] font-[500] text-[#161827]">
                            {business.name}
                          </div>
                          <div className="truncate text-[14px] text-[#6E7397]">
                            {business.email ?? business.user?.email ?? "No email"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="text-[16px] font-[500] text-[#2C2F44]">
          {selectedBusinessIds.length} selected
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            className="h-11 rounded-[14px] px-6"
            onClick={onReassignCustomers}
            disabled={selectedBusinessIds.length === 0}
          >
            Reassign customers...
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ManageMemberCustomersModal;
